import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, ArrowLeft, Phone, BookOpen, Calendar, Share2, BadgeCheck, UtensilsCrossed as UtensilsCrossedIcon, Dumbbell as DumbbellIcon, BarChart3 as BarChartIcon } from 'lucide-react';
import { blogPosts, getPostDate } from './Blog';
import SEOHead from '../components/SEOHead';
import { trackBlogPostRead, setFunnelPosition } from '../utils/analytics';
import DOMPurify from 'dompurify';

// Import all blog content
import { blogContent } from '../content/blogContent';
import { sermorelinBlogPosts, SERMORELIN_FORECAST_URL } from '../content/sermorelinBlogContent';
import { viralBlogPosts, VIRAL_FORECAST_URL, BRYAN_PHONE } from '../content/viralBlogContent';
import { peptideBlogPosts } from '../content/peptideBlogContent';

// Forecast Bridge Components
const MidPostBridge = () => (
  <Card className="bg-[#3B82F6]/10 border-[#3B82F6]/30 p-6 my-8">
    <p className="text-[#F8FAFC] text-sm leading-relaxed">
      Everyone's response to GLP-1 medications is different — timeline, results, and experience all depend on your specific profile. If you're curious what a realistic estimate looks like for your situation, the forecast tool at{' '}
      <Link to="/calculator" className="text-[#3B82F6] hover:underline font-medium">
        our Calculator page
      </Link>{' '}
      builds a personalized 90-day picture based on your inputs — no call required to see it.
    </p>
  </Card>
);

// Sermorelin Mid-Post Bridge - links to external myweightforecast.com
const SermorelinMidPostBridge = () => (
  <Card className="bg-[#F97316]/10 border-[#F97316]/30 p-6 my-8">
    <p className="text-[#F8FAFC] text-sm leading-relaxed">
      Everyone's wellness picture is different — what works best depends on your specific profile, goals, and where you are in your health journey. If you want to see what a personalized program could look like for you, the forecast tool at{' '}
      <a href={SERMORELIN_FORECAST_URL} target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline font-medium">
        MyWeightForecast.com
      </a>{' '}
      builds a 90-day estimate based on your inputs in about 3 minutes — no consultation required to see your results.
    </p>
  </Card>
);

const EndCard = () => (
  <Card className="bg-gradient-to-r from-[#3B82F6]/10 to-[#22C55E]/10 border-[#3B82F6]/30 p-8 my-8">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
        <Calendar size={24} className="text-[#3B82F6]" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">See Your Personal 90-Day Forecast</h3>
        <p className="text-[#94A3B8]">
          The information above applies to most people — but your results depend on factors specific to you.{' '}
          <Link to="/calculator" className="text-[#3B82F6] hover:underline">Our 90-Day Forecast Calculator</Link>{' '}
          builds a personalized estimate based on your profile in about 3 minutes.
        </p>
      </div>
    </div>
    <Link
      to="/calculator"
      className="inline-flex items-center justify-center w-full sm:w-auto bg-[#22C55E] hover:bg-[#16A34A] text-white font-medium px-6 py-3 rounded-md transition-colors"
    >
      Start My 90-Day Forecast
      <ArrowRight className="ml-2" size={18} />
    </Link>
    <p className="text-[#64748B] text-xs mt-3">Takes about 2–3 minutes — no call required</p>
  </Card>
);

// Sermorelin End Card - links to external myweightforecast.com
const SermorelinEndCard = () => (
  <Card className="bg-gradient-to-r from-[#F97316]/10 to-[#22C55E]/10 border-[#F97316]/30 p-8 my-8">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-[#F97316]/20 flex items-center justify-center flex-shrink-0">
        <Calendar size={24} className="text-[#F97316]" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">See Your Personal 90-Day Forecast</h3>
        <p className="text-[#94A3B8]">
          Your goals are specific to you — your program should be too.{' '}
          <a href={SERMORELIN_FORECAST_URL} target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">MyWeightForecast.com</a>{' '}
          builds a personalized estimate based on your profile in about 3 minutes.
        </p>
      </div>
    </div>
    <a
      href={SERMORELIN_FORECAST_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-full sm:w-auto bg-[#F97316] hover:bg-[#EA580C] text-white font-medium px-6 py-3 rounded-md transition-colors"
    >
      Build My Forecast
      <ArrowRight className="ml-2" size={18} />
    </a>
    <p className="text-[#64748B] text-xs mt-3">No consultation required to view results</p>
  </Card>
);

// Viral Crossover Mid-Post Bridge - per-post copy, links to external myweightforecast.com
const ViralMidPostBridge = ({ text }) => (
  <Card className="bg-[#3B82F6]/10 border-[#3B82F6]/30 p-6 my-8">
    <p className="text-[#F8FAFC] text-sm leading-relaxed">
      {text}{' '}
      <a href={VIRAL_FORECAST_URL} target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline font-medium">
        Build Your Forecast at MyWeightForecast.com
      </a>
    </p>
  </Card>
);

// Viral Crossover End Card - per-post copy + text-Bryan line + compliance disclaimer
const ViralEndCard = ({ text, textBryanPrompt, disclaimer }) => (
  <Card className="bg-gradient-to-r from-[#3B82F6]/10 to-[#22C55E]/10 border-[#3B82F6]/30 p-8 my-8">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
        <Calendar size={24} className="text-[#3B82F6]" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">See Your Personal 90-Day Forecast</h3>
        <p className="text-[#94A3B8]">{text}</p>
      </div>
    </div>
    <a
      href={VIRAL_FORECAST_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-full sm:w-auto bg-[#22C55E] hover:bg-[#16A34A] text-white font-medium px-6 py-3 rounded-md transition-colors"
    >
      Build My Forecast
      <ArrowRight className="ml-2" size={18} />
    </a>
    <p className="text-[#94A3B8] text-sm mt-4">
      {textBryanPrompt}{' '}
      <a href={`tel:${BRYAN_PHONE}`} className="text-[#22C55E] hover:underline font-medium">{BRYAN_PHONE}</a>
    </p>
    <p className="text-[#64748B] text-xs mt-4 leading-relaxed border-t border-[#1E293B] pt-4">
      {disclaimer}
    </p>
  </Card>
);

// Category colors
const categoryColors = {
  'Foundation': 'bg-[#3B82F6]/20 text-[#3B82F6]',
  'Diet & Food': 'bg-[#22C55E]/20 text-[#22C55E]',
  'Side Effects': 'bg-[#F59E0B]/20 text-[#F59E0B]',
  'Lifestyle': 'bg-[#8B5CF6]/20 text-[#8B5CF6]',
  'Getting Started': 'bg-[#EC4899]/20 text-[#EC4899]',
  'Results & Timelines': 'bg-[#06B6D4]/20 text-[#06B6D4]',
  'Troubleshooting': 'bg-[#EF4444]/20 text-[#EF4444]',
  'Comparisons': 'bg-[#14B8A6]/20 text-[#14B8A6]',
  'NAD+': 'bg-[#A855F7]/20 text-[#A855F7]',
  'Education': 'bg-[#6366F1]/20 text-[#6366F1]',
  'Sermorelin': 'bg-[#F97316]/20 text-[#F97316]',
  "Women's Health": 'bg-[#DB2777]/20 text-[#DB2777]',
  'Recovery & Healing': 'bg-[#10B981]/20 text-[#10B981]',
  'Energy & Cellular Health': 'bg-[#EAB308]/20 text-[#EAB308]',
  'Sleep': 'bg-[#818CF8]/20 text-[#818CF8]',
  "Men's Health": 'bg-[#0EA5E9]/20 text-[#0EA5E9]'
};

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find the post metadata
  const post = blogPosts.find(p => p.slug === slug);
  
  // Check if it's a Sermorelin post
  const isSermorelin = post?.category === 'Sermorelin';
  const sermorelinPost = isSermorelin ? sermorelinBlogPosts.find(p => p.slug === slug) : null;

  // Check if it's a Viral Crossover post (detected by slug, since categories overlap)
  const viralPost = viralBlogPosts.find(p => p.slug === slug);
  const isViral = Boolean(viralPost);

  // Check if it's a Peptide post (detected by slug, since categories overlap)
  const peptidePost = peptideBlogPosts.find(p => p.slug === slug);
  const isPeptide = Boolean(peptidePost);

  // HTML-style posts (viral + peptide) share the same renderer
  const htmlPost = viralPost || peptidePost;
  const isHtmlPost = isViral || isPeptide;

  // "What You'll Learn" bullets (peptide posts only, for now)
  const whatYoullLearn = peptidePost?.whatYoullLearn || null;

  // Get content from appropriate source
  const content = isHtmlPost
    ? (htmlPost?.content || null)
    : isSermorelin
      ? (sermorelinPost?.content || null)
      : (blogContent ? blogContent[slug] : null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Get related posts (same category, excluding current)
  const relatedPosts = post 
    ? blogPosts.filter(p => p.category === post.category && p.slug !== slug).slice(0, 2)
    : [];

  // Track blog view and send to backend
  useEffect(() => {
    if (post && slug) {
      // Track in GA4 and set funnel position
      trackBlogPostRead(slug, post.title, post.category);
      setFunnelPosition(1);
      
      // Send to backend for analytics
      fetch(`${BACKEND_URL}/api/admin/blog-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slug,
          title: post.title,
          category: post.category
        })
      }).catch(err => console.log('Blog view tracking failed:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, BACKEND_URL]);

  // Set document title (kept for fallback)
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Bryan Maldonado`;
    } else {
      document.title = 'Article Not Found | Bryan Maldonado';
    }
    return () => {
      document.title = 'Bryan Maldonado';
    };
  }, [post]);

  if (!post || !content) {
    return (
      <div className="min-h-screen bg-[#0B1020] flex items-center justify-center">
        <Card className="bg-[#121A2B] border-[#1E293B] p-8 text-center">
          <h1 className="text-2xl font-bold text-[#F8FAFC] mb-4">Article Not Found</h1>
          <p className="text-[#94A3B8] mb-6">The article you're looking for doesn't exist or has been moved.</p>
          <Button onClick={() => navigate('/blog')} className="bg-[#3B82F6] hover:bg-[#2563EB]">
            <ArrowLeft className="mr-2" size={18} />
            Back to Blog
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1020]">
      <SEOHead 
        title={post.title}
        description={post.description}
        canonicalPath={`/blog/${slug}`}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1020]/95 backdrop-blur-sm border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-[#F8FAFC]">Bryan Maldonado</Link>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="hidden sm:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <BookOpen size={18} />
                <span>All Articles</span>
              </Link>
              <Button onClick={() => navigate('/calculator')} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                90-Day Forecast
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-6">
            <Link to="/blog" className="text-[#94A3B8] hover:text-[#F8FAFC]">Blog</Link>
            <span className="text-[#64748B]">/</span>
            <span className={`px-2 py-1 rounded text-xs ${categoryColors[post.category]}`}>
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="text-[#94A3B8] text-sm mb-2">
            {post.readTime}
          </div>
          <div className="mb-2">
            <p className="text-sm flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="text-[#F8FAFC] font-semibold">Reviewed by Bryan Maldonado</span>
              <BadgeCheck className="text-[#22C55E] flex-shrink-0" size={15} />
              <span className="text-[#94A3B8]">Patient Success Specialist</span>
            </p>
            <p className="text-[#94A3B8] text-xs mt-1">Based on common patient experiences and educational resources</p>
          </div>
          <p className="text-[#64748B] text-sm mb-8">Updated {getPostDate(slug, post.category)}</p>

          {/* What You'll Learn */}
          {whatYoullLearn && whatYoullLearn.length > 0 && (
            <Card className="bg-[#121A2B] border-[#1E293B] p-6 mb-8" data-testid="what-youll-learn">
              <h2 className="text-lg font-bold text-[#F8FAFC] mb-4">What You'll Learn</h2>
              <ul className="space-y-2">
                {whatYoullLearn.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#CBD5E1] text-sm">
                    <span className="text-[#3B82F6] mt-1 flex-shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            {isHtmlPost ? (
              // Viral + Peptide posts: HTML content split at <!--MIDBRIDGE--> with per-post bridge + end card
              <>
                <div
                  className="sermorelin-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.split('<!--MIDBRIDGE-->')[0]) }}
                />
                <ViralMidPostBridge text={htmlPost.midBridgeText} />
                <div
                  className="sermorelin-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.split('<!--MIDBRIDGE-->')[1] || '') }}
                />
                <ViralEndCard
                  text={htmlPost.endCardText}
                  textBryanPrompt={htmlPost.textBryanPrompt}
                  disclaimer={htmlPost.disclaimer}
                />
              </>
            ) : isSermorelin ? (
              // Sermorelin posts use HTML content
              <>
                <div 
                  className="sermorelin-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.split('</h2>').slice(0, 3).join('</h2>')) }}
                />
                <SermorelinMidPostBridge />
                <div 
                  className="sermorelin-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.split('</h2>').slice(3).join('</h2>')) }}
                />
                <SermorelinEndCard />
              </>
            ) : (
              // Regular blog posts use structured sections
              <>
                {content.sections.map((section, idx) => (
                  <React.Fragment key={idx}>
                    {section.type === 'text' && (
                      <p className="text-[#CBD5E1] leading-relaxed mb-6">{section.content}</p>
                    )}
                    {section.type === 'heading' && (
                      <h2 className="text-2xl font-bold text-[#F8FAFC] mt-10 mb-4">{section.content}</h2>
                    )}
                    {section.type === 'subheading' && (
                      <h3 className="text-xl font-bold text-[#F8FAFC] mt-8 mb-3">{section.content}</h3>
                    )}
                    {section.type === 'list' && (
                      <ul className="space-y-2 mb-6">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-[#CBD5E1]">
                            <span className="text-[#3B82F6] mt-1">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.type === 'bold-list' && (
                      <ul className="space-y-3 mb-6">
                        {section.items.map((item, i) => (
                          <li key={i} className="text-[#CBD5E1]">
                            <strong className="text-[#F8FAFC]">{item.title}</strong>
                            {item.desc && <span> — {item.desc}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.type === 'highlight' && (
                      <Card className="bg-[#1E293B]/50 border-[#1E293B] p-4 mb-6">
                        <p className="text-[#F8FAFC] font-medium">{section.content}</p>
                      </Card>
                    )}
                    {section.type === 'mid-bridge' && <MidPostBridge />}
                  </React.Fragment>
                ))}
              </>
            )}

            {/* FAQ Section - only for structured (non-Sermorelin, non-HTML) posts */}
            {!isSermorelin && !isHtmlPost && content.faq && content.faq.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">Frequently Asked Questions</h2>
                <div className="space-y-6">
                  {content.faq.map((item, idx) => (
                    <div key={idx} className="border-b border-[#1E293B] pb-6">
                      <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">{item.q}</h3>
                      <p className="text-[#94A3B8]">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* End Card - only for structured posts (Sermorelin and HTML posts have their own) */}
            {!isSermorelin && !isHtmlPost && <EndCard />}

            {/* Free Tools Card */}
            <Card className="bg-[#121A2B] border-[#1E293B] p-6 my-8" data-testid="blog-free-tools">
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-4">Free Tools</h3>
              <div className="space-y-2">
                <Link to="/meal-plan" className="flex items-center justify-between gap-3 bg-[#0B1020] rounded-lg p-3 hover:bg-[#1E293B] transition-colors group" data-testid="blog-tool-meal">
                  <span className="flex items-center gap-3 text-[#E2E8F0] text-sm">
                    <UtensilsCrossedIcon className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                    7-Day Meal Plan Generator
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/strength-plan" className="flex items-center justify-between gap-3 bg-[#0B1020] rounded-lg p-3 hover:bg-[#1E293B] transition-colors group" data-testid="blog-tool-strength">
                  <span className="flex items-center gap-3 text-[#E2E8F0] text-sm">
                    <DumbbellIcon className="w-5 h-5 text-[#A855F7] flex-shrink-0" />
                    GLP-1 Strength Plan Builder
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/calculator" className="flex items-center justify-between gap-3 bg-[#0B1020] rounded-lg p-3 hover:bg-[#1E293B] transition-colors group" data-testid="blog-tool-forecast">
                  <span className="flex items-center gap-3 text-[#E2E8F0] text-sm">
                    <BarChartIcon className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    90-Day Forecast Calculator
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-6">Related Articles</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedPosts.map((relPost) => (
                <Link
                  key={relPost.slug}
                  to={`/blog/${relPost.slug}`}
                  className="group"
                >
                  <Card className="bg-[#121A2B] border-[#1E293B] p-4 h-full hover:border-[#3B82F6] transition-all duration-300">
                    <span className={`inline-block px-2 py-1 rounded text-xs mb-2 ${categoryColors[relPost.category]}`}>
                      {relPost.category}
                    </span>
                    <h3 className="text-[#F8FAFC] font-medium group-hover:text-[#3B82F6] transition-colors">
                      {relPost.title}
                    </h3>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-3xl mx-auto text-center">
          <Button
            onClick={() => navigate('/blog')}
            variant="outline"
            className="border-[#1E293B] bg-transparent hover:bg-[#1E293B] text-[#F8FAFC]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to All Articles
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#94A3B8] mb-4">© 2024 Bryan Maldonado. All rights reserved.</p>
          <p className="text-[#64748B] text-sm max-w-2xl mx-auto">
            All the information on this site is educational. Nothing here constitutes medical advice. Bryan Maldonado is a licensed professional and all programs include medical oversight.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
